import React, { useState, useEffect } from 'react';
import {
    Box, Heading, VStack, FormControl, FormLabel,
    Input, Select, Textarea, Button, useToast,
    Alert, AlertIcon, Card, CardBody, Text, Spinner, Center,
    Flex, HStack, Badge, Divider
} from '@chakra-ui/react';
import { FiCalendar, FiCheckCircle } from 'react-icons/fi';
import { authAPI } from '../services/api';
import { useNavigate } from 'react-router-dom';
import LoadingOverlay from '../components/LoadingOverlay';
import LogoPulseLoader from '../components/LogoPulseLoader';

const ParentLeavePage = () => {
    const [children, setChildren] = useState([]);
    const todayStr = new Intl.DateTimeFormat('en-CA', {
        timeZone: 'Asia/Taipei',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit'
    }).format(new Date());

    const [formData, setFormData] = useState({
        student_id: '',
        startDate: todayStr,
        endDate: todayStr,
        type: 'PERSONAL',
        reason: '',
        targetClassId: '' // '' means All
    });
    const [existingLeaves, setExistingLeaves] = useState([]);
    const [loading, setLoading] = useState(false);
    const [initialLoading, setInitialLoading] = useState(true);
    const toast = useToast();
    const navigate = useNavigate();

    // Derived: Current Student's Classes
    const currentChild = children.find(c => c.id == formData.student_id);
    const studentClasses = currentChild ? (currentChild.classes || []) : [];
    // Fallback for V1
    if (currentChild && currentChild.class && !studentClasses.find(c => c.id === currentChild.class.id)) {
        studentClasses.push(currentChild.class);
    }

    useEffect(() => {
        fetchChildren();
    }, []);

    // Refresh leaves when child changes
    useEffect(() => {
        if (formData.student_id) {
            fetchExistingLeaves(formData.student_id);
            setFormData(prev => ({ ...prev, targetClassId: '' })); // Reset class selection
        }
    }, [formData.student_id]);

    const fetchChildren = async () => {
        try {
            const res = await authAPI.getMe();
            if (res.data.children && res.data.children.length > 0) {
                setChildren(res.data.children);
                // Default to first child
                setFormData(prev => ({ ...prev, student_id: res.data.children[0].id }));
            }
        } catch (error) {
            console.error(error);
            toast({ title: "無法載入學生資料", status: "error" });
        } finally {
            setInitialLoading(false);
        }
    };

    const fetchExistingLeaves = async (studentId) => {
        try {
            // Get leaves from today onwards
            const res = await authAPI.leaves.getLeaves({
                student_id: studentId,
                start_date: todayStr
            });
            // API returns list directly
            setExistingLeaves(res.data);
        } catch (error) {
            console.error("Failed to fetch leaves", error);
        }
    };

    const handleSubmit = async () => {
        if (!formData.student_id) {
            toast({ title: "請選擇學生", status: "warning" });
            return;
        }

        const start = new Date(formData.startDate);
        const end = new Date(formData.endDate);

        if (end < start) {
            toast({ title: "結束日期不能早於開始日期", status: "warning" });
            return;
        }

        setLoading(true);
        try {
            // Smart Logic: Calculate Valid Dates First
            const calcRes = await authAPI.leaves.calculateDates({
                student_id: formData.student_id,
                start_date: formData.startDate,
                end_date: formData.endDate,
                class_id: formData.targetClassId || null
            });

            const validDates = calcRes.data.valid_dates;
            const scheduleInfo = calcRes.data.schedule_info;

            if (validDates.length === 0) {
                toast({ title: "所選日期區間無課程/無需請假", status: "info" });
                setLoading(false);
                return;
            }

            // Confirmation Logic
            const confirmMsg = `
系統依據課表 (${scheduleInfo}) 檢測與計算：
您選取的期間內共有 ${validDates.length} 個上課日需請假：
${validDates.join(', ')}

確定要送出申請嗎？`.trim();

            if (!window.confirm(confirmMsg)) {
                setLoading(false);
                return;
            }

            // Submit leaves
            await Promise.all(validDates.map(dateStr =>
                authAPI.leaves.createLeave({
                    student_id: formData.student_id,
                    date: dateStr,
                    type: formData.type,
                    reason: formData.reason,
                    class_id: formData.targetClassId ? Number(formData.targetClassId) : null // 僅請特定課程時帶；空=整天請假
                })
            ));

            toast({
                title: "請假申請已送出",
                description: `已為 ${validDates.length} 個日期提交申請`,
                status: "success",
                duration: 3000
            });

            // Refresh list
            fetchExistingLeaves(formData.student_id);

        } catch (error) {
            console.error(error);
            toast({
                title: "申請失敗",
                description: error.response?.data?.detail || "請稍後再試",
                status: "error"
            });
        } finally {
            setLoading(false);
        }
    };

    const handleCancel = async (leaveId) => {
        if (!window.confirm("確定要取消此請假申請嗎？")) return;

        try {
            await authAPI.leaves.deleteLeave(leaveId);
            toast({ title: "已取消", status: "success" });
            fetchExistingLeaves(formData.student_id);
        } catch (error) {
            toast({ title: "取消失敗", status: "error" });
        }
    };

    return (
        <VStack spacing={6} align="stretch" p={5} pb="150px">
            {initialLoading ? (
                <Flex justify="center" align="center" py={10}>
                    <LogoPulseLoader />
                </Flex>
            ) : children.length === 0 ? (
                <Alert status="warning" borderRadius="xl">
                    <AlertIcon />
                    目前尚無綁定的子女資料，無法申請請假。
                </Alert>
            ) : (
                <>
                    {/* Application Form */}
                    <Card bg="white" shadow="sm" borderRadius="2xl" border="none">
                        <CardBody p={6}>
                            <VStack spacing={6} align="stretch">
                                {/* Student Selector */}
                                <FormControl>
                                    <FormLabel fontSize="lg" fontWeight="bold" color="gray.600">申請學生</FormLabel>
                                    <Select
                                        size="lg"
                                        h="50px"
                                        fontSize="lg"
                                        value={formData.student_id}
                                        onChange={e => setFormData({ ...formData, student_id: e.target.value })}
                                        borderRadius="xl"
                                        bg="gray.50"
                                        border="none"
                                        iconColor="gray.400"
                                    >
                                        {children.map(child => (
                                            <option key={child.id} value={child.id}>
                                                {child.full_name} ({child.class?.name || '未分班'})
                                            </option>
                                        ))}
                                    </Select>
                                </FormControl>

                                {/* Class Filter (Optional) */}
                                <FormControl>
                                    <HStack justify="space-between">
                                        <FormLabel fontSize="md" color="gray.500" mb={0}>僅請特定課程 (選填)</FormLabel>
                                    </HStack>
                                    <Select
                                        size="md"
                                        mt={2}
                                        borderRadius="xl"
                                        value={formData.targetClassId}
                                        onChange={e => setFormData({ ...formData, targetClassId: e.target.value })}
                                        bg="gray.50"
                                        border="none"
                                        placeholder="全部班級 (預設)"
                                    >
                                        {studentClasses.map(cls => (
                                            <option key={cls.id} value={cls.id}>
                                                僅 {cls.name} ({cls.department === 'English' ? '英文' : '安親'})
                                            </option>
                                        ))}
                                    </Select>
                                </FormControl>

                                {/* Date Range */}
                                <Box>
                                    <FormLabel fontSize="lg" fontWeight="bold" color="gray.600">日期區間</FormLabel>
                                    <Flex direction={{ base: 'column', md: 'row' }} gap={3} align="center">
                                        <Input
                                            type="date"
                                            size="lg"
                                            h="50px"
                                            w="100%"
                                            borderRadius="xl"
                                            bg="gray.50"
                                            border="none"
                                            value={formData.startDate}
                                            min={todayStr}
                                            onChange={e => {
                                                const newDate = e.target.value;
                                                setFormData(prev => ({
                                                    ...prev,
                                                    startDate: newDate,
                                                    endDate: prev.endDate < newDate ? newDate : prev.endDate
                                                }));
                                            }}
                                        />
                                        <Text color="gray.400" whiteSpace="nowrap">至</Text>
                                        <Input
                                            type="date"
                                            size="lg"
                                            h="50px"
                                            w="100%"
                                            borderRadius="xl"
                                            bg="gray.50"
                                            border="none"
                                            value={formData.endDate}
                                            min={formData.startDate}
                                            onChange={e => setFormData({ ...formData, endDate: e.target.value })}
                                        />
                                    </Flex>
                                </Box>

                                {/* Leave Type - Button Group */}
                                <FormControl>
                                    <FormLabel fontSize="lg" fontWeight="bold" color="gray.600">假別</FormLabel>
                                    <HStack spacing={3} w="100%">
                                        {['PERSONAL', 'SICK', 'OTHER'].map(type => (
                                            <Button
                                                key={type}
                                                flex={1}
                                                h="50px"
                                                borderRadius="xl"
                                                variant={formData.type === type ? 'solid' : 'outline'}
                                                colorScheme={formData.type === type ? (type === 'SICK' ? 'red' : 'brand') : 'gray'}
                                                border={formData.type === type ? 'none' : '1px solid'}
                                                borderColor="gray.200"
                                                onClick={() => setFormData({ ...formData, type })}
                                                _active={{ transform: 'scale(0.96)' }}
                                                fontSize="lg"
                                            >
                                                {type === 'PERSONAL' ? '事假' : type === 'SICK' ? '病假' : '其他'}
                                            </Button>
                                        ))}
                                    </HStack>
                                </FormControl>

                                {/* Reason */}
                                <FormControl>
                                    <FormLabel fontSize="lg" fontWeight="bold" color="gray.600">原因</FormLabel>
                                    <Textarea
                                        placeholder="請輸入請假原因..."
                                        value={formData.reason}
                                        onChange={e => setFormData({ ...formData, reason: e.target.value })}
                                        minH="120px"
                                        borderRadius="xl"
                                        bg="gray.50"
                                        border="none"
                                        fontSize="lg"
                                        p={4}
                                    />
                                </FormControl>

                                {/* Submit Button */}
                                <Button
                                    w="100%"
                                    h="60px"
                                    colorScheme="brand"
                                    size="lg"
                                    fontSize="xl"
                                    bgGradient="linear(to-r, brand.500, brand.400)"
                                    borderRadius="xl"
                                    shadow="md"
                                    leftIcon={<FiCheckCircle />}
                                    isLoading={loading}
                                    onClick={handleSubmit}
                                    _active={{ transform: 'scale(0.98)' }}
                                    _hover={{ shadow: 'lg' }}
                                >
                                    送出登記
                                </Button>
                            </VStack>
                        </CardBody>
                    </Card>

                    {/* History Section */}
                    {existingLeaves.length > 0 && (
                        <Box>
                            <Text fontSize="lg" fontWeight="bold" mb={4} px={2} color="gray.500">最近申請紀錄</Text>
                            <Card bg="white" shadow="sm" borderRadius="2xl" border="none" overflow="hidden">
                                <VStack spacing={0} align="stretch" divider={<Divider borderColor="gray.100" />}>
                                    {existingLeaves.map((leave, index) => (
                                        <Box key={leave.id} p={5} _active={{ bg: 'gray.50' }} transition="all 0.2s">
                                            <Flex justify="space-between" align="start">
                                                <VStack align="start" spacing={2} flex={1}>
                                                    <HStack>
                                                        <Badge
                                                            colorScheme={
                                                                leave.status === 'APPROVED' ? 'green' :
                                                                    leave.status === 'REJECTED' ? 'red' : 'orange'
                                                            }
                                                            px={2} py={1} borderRadius="md"
                                                        >
                                                            {leave.status === 'APPROVED' ? '已核准' :
                                                                leave.status === 'REJECTED' ? '已拒絕' : '審核中'}
                                                        </Badge>
                                                        <Badge colorScheme={leave.type === 'SICK' ? 'red' : 'blue'} variant="subtle">
                                                            {leave.type === 'PERSONAL' ? '事假' : leave.type === 'SICK' ? '病假' : '其他'}
                                                        </Badge>
                                                    </HStack>

                                                    <HStack color="gray.800">
                                                        <Box as={FiCalendar} />
                                                        <Text fontWeight="bold" fontSize="lg">{leave.date}</Text>
                                                    </HStack>

                                                    {leave.reason && (
                                                        <Text color="gray.500" fontSize="md" noOfLines={2}>
                                                            {leave.reason}
                                                        </Text>
                                                    )}
                                                </VStack>

                                                {(!leave.status || leave.status === 'PENDING') && (
                                                    <Button
                                                        size="sm"
                                                        colorScheme="red"
                                                        variant="ghost"
                                                        onClick={() => handleCancel(leave.id)}
                                                    >
                                                        取消
                                                    </Button>
                                                )}
                                            </Flex>
                                        </Box>
                                    ))}
                                </VStack>
                            </Card>
                        </Box>
                    )}
                </>
            )}
        </VStack>
    );
};

export default ParentLeavePage;
